import React from 'react';
import {Select} from '@mantine/core';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {WorkflowTrainingEventTypeEnum} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import {useGetCourseDefinitionSelectOptionsQuery} from 'src/redux/queries/courseDefinition';
import type {TriggerEditorProps} from './TriggerCard';
import {SectionLabel} from './SectionLabel';

const trainingEventConfigs = [
  {
    type: WorkflowTrainingEventTypeEnum.Completed,
    label: 'Completed',
    description: 'Trigger when the trainee completes the course.',
  },
  {
    type: WorkflowTrainingEventTypeEnum.Failed,
    label: 'Failed',
    description: 'Trigger when the trainee fails the course.',
  },
];

export const TriggerTrainingEditor: React.FC<TriggerEditorProps> = ({
  trigger,
  onTriggerChange,
}) => {
  const {data, isLoading} = useGetCourseDefinitionSelectOptionsQuery({
    start: 0,
    length: 100,
    active: true,
  });

  const courseOptions =
    data?.data?.map(c => ({
      value: String(c.id),
      label: c.label ?? String(c.id),
    })) ?? [];

  const handleCourseChange = (value: string | null) => {
    onTriggerChange({
      ...trigger,
      triggerCourseDefinitionId: value ? Number(value) : undefined,
    });
  };

  const handleEventTypeChange = (value: string) => {
    onTriggerChange({
      ...trigger,
      triggerTrainingEventType: Number(
        value
      ) as WorkflowTrainingEventTypeEnum,
    });
  };

  return (
    <Stack gap="md">
      <Select
        label="Course"
        description="Select the training course that will trigger this workflow."
        data={courseOptions}
        value={
          trigger.triggerCourseDefinitionId
            ? String(trigger.triggerCourseDefinitionId)
            : null
        }
        onChange={handleCourseChange}
        disabled={isLoading}
        searchable
      />

      <SectionLabel label="Event Type" />
      <SimpleGrid cols={{base: 1, sm: 2}}>
        {trainingEventConfigs.map(c => (
          <SelectableCard
            key={c.type}
            label={c.label}
            description={c.description}
            value={String(c.type)}
            selected={trigger.triggerTrainingEventType === c.type}
            onClick={handleEventTypeChange}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
};
